import React, { FC, useCallback, useEffect, useState } from 'react';
import { Chip, FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { createUseStyles } from 'src/theming';
import { LegacyContact } from 'src/interfaces/api/Contact';
import useAuth from 'src/hooks/api/useAuth';

interface Props {
  className?: any;
  customerId: number;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  label?: string;
  onChange?: (value?: number[]) => void;
  value?: number[];
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const useStyles = createUseStyles({
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: 2,
  },
});

const InterlocutorsSelect: FC<Props> = React.forwardRef<any, Props>(
  ({ value, onChange, disabled, customerId, className, helperText, ...props }, ref) => {
    const [contacts, setContacts] = useState<LegacyContact[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const { fetch } = useAuth();
    const classes = useStyles();

    const fetchInterlocutors = (customerId: number): Promise<LegacyContact[]> => {
      const queryURL = '/api/customer/show/';
      const graphQL = '{ customer(id:' + customerId + '){ contacts{id, name, name, lastname}}}';

      return fetch(queryURL + graphQL, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'GET',
      })
        .then((resp) => resp.json())
        .then((data) => {
          if (data['data']['customer']['contacts']) {
            return data['data']['customer']['contacts'];
          }

          throw new Error('Aucun contact trouvé ...');
        });
    };

    const getContact = useCallback((contactId: number) => contacts.find((contact) => contact.id === contactId), [
      contacts,
    ]);

    const getContactLabel = useCallback(
      (contactId: number) => {
        const contact = getContact(contactId);
        return `${contact?.name} ${contact?.lastname}`;
      },
      [contacts]
    );

    useEffect(() => {
      fetchInterlocutors(customerId)
        .then((contacts) => {
          setContacts(contacts);
          setLoading(false);
        })
        .catch((e) => {
          setError(e.message);
          setLoading(false);
        });
    }, [customerId]);

    const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
      const newValue = event.target.value;
      onChange && onChange(newValue as number[]);
    };

    if (loading) {
      return <Skeleton height={50} />;
    }

    if (error) {
      return <div style={{ color: '#f44336' }}>{error}</div>;
    }

    return (
      <FormControl className={className} ref={ref} variant='outlined' error={props.error} fullWidth>
        <InputLabel>Interlocuteurs</InputLabel>
        <Select
          value={value || ''}
          multiple
          label='Interlocuteurs'
          onChange={handleChange}
          MenuProps={MenuProps}
          disabled={disabled}
          renderValue={(selected) =>
            Array.isArray(selected) ? (
              <div className={classes.chips}>
                {(selected as number[]).map((value) => (
                  <Chip key={value} label={getContactLabel(value)} className={classes.chip} />
                ))}
              </div>
            ) : (
              <div>{getContactLabel(selected as number)}</div>
            )
          }
        >
          {contacts.map((contact) => (
            <MenuItem key={contact.id} value={contact.id}>
              {getContactLabel(contact.id)}
            </MenuItem>
          ))}
        </Select>

        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

export default React.memo(InterlocutorsSelect);
